/**
 * The AR module's route table.
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads them in. Keeping them here means the paths, the
 * permissions and the components that implement them are decided in one place by
 * whoever owns the module.
 *
 * Ordering matters in two ways and both are load-bearing:
 *
 *  - `/ar/invoices/new` is declared **before** `/ar/invoices/:id(\d+)` so `new`
 *    can never be parsed as a document id. The `\d+` constraint makes that safe
 *    anyway; the ordering makes it obvious.
 *  - Credit notes reuse the invoice list and document screens with a `docKind`
 *    prop rather than a copy. They are the same document in the same table
 *    (`ar_invoices.doc_kind`), differing in which side of the ledger they land
 *    on, and two screens that drift apart would eventually disagree about what a
 *    document is.
 */

import type { RouteRecordRaw } from 'vue-router'

import { AR_PERMISSIONS } from '@/modules/ar/permissions'

export const AR_ROUTES: RouteRecordRaw[] = [
  {
    path: '/ar/customers',
    name: 'ar-customers',
    component: () => import('@/modules/ar/CustomersListView.vue'),
    meta: { titleKey: 'ar.routeTitles.arCustomers', permission: AR_PERMISSIONS.customerRead },
  },
  {
    path: '/ar/customers/:id(\\d+)',
    name: 'ar-customer',
    component: () => import('@/modules/ar/CustomerView.vue'),
    meta: { titleKey: 'ar.routeTitles.arCustomer', permission: AR_PERMISSIONS.customerRead },
  },

  {
    path: '/ar/invoices',
    name: 'ar-invoices',
    component: () => import('@/modules/ar/InvoicesListView.vue'),
    props: { docKind: 'invoice' },
    meta: { titleKey: 'ar.routeTitles.arInvoices', permission: AR_PERMISSIONS.invoiceRead },
  },
  {
    path: '/ar/invoices/new',
    name: 'ar-invoice-new',
    component: () => import('@/modules/ar/InvoiceView.vue'),
    props: { docKind: 'invoice' },
    meta: { titleKey: 'ar.routeTitles.arInvoiceNew', permission: AR_PERMISSIONS.invoiceCreate },
  },
  {
    path: '/ar/invoices/:id(\\d+)',
    name: 'ar-invoice',
    component: () => import('@/modules/ar/InvoiceView.vue'),
    props: { docKind: 'invoice' },
    meta: { titleKey: 'ar.routeTitles.arInvoice', permission: AR_PERMISSIONS.invoiceRead },
  },

  {
    path: '/ar/credit-notes',
    name: 'ar-credit-notes',
    component: () => import('@/modules/ar/InvoicesListView.vue'),
    props: { docKind: 'credit_note' },
    meta: { titleKey: 'ar.routeTitles.arCreditNotes', permission: AR_PERMISSIONS.creditNoteRead },
  },
  {
    path: '/ar/credit-notes/new',
    name: 'ar-credit-note-new',
    component: () => import('@/modules/ar/InvoiceView.vue'),
    props: { docKind: 'credit_note' },
    meta: { titleKey: 'ar.routeTitles.arCreditNoteNew', permission: AR_PERMISSIONS.creditNoteCreate },
  },
  {
    path: '/ar/credit-notes/:id(\\d+)',
    name: 'ar-credit-note',
    component: () => import('@/modules/ar/InvoiceView.vue'),
    props: { docKind: 'credit_note' },
    meta: { titleKey: 'ar.routeTitles.arCreditNote', permission: AR_PERMISSIONS.creditNoteRead },
  },

  {
    path: '/ar/receipts',
    name: 'ar-receipts',
    component: () => import('@/modules/ar/ReceiptsListView.vue'),
    meta: { titleKey: 'ar.routeTitles.arReceipts', permission: AR_PERMISSIONS.receiptRead },
  },
  {
    path: '/ar/receipts/new',
    name: 'ar-receipt-new',
    component: () => import('@/modules/ar/ReceiptView.vue'),
    meta: { titleKey: 'ar.routeTitles.arReceiptNew', permission: AR_PERMISSIONS.receiptCreate },
  },
  {
    path: '/ar/receipts/:id(\\d+)',
    name: 'ar-receipt',
    component: () => import('@/modules/ar/ReceiptView.vue'),
    meta: { titleKey: 'ar.routeTitles.arReceipt', permission: AR_PERMISSIONS.receiptRead },
  },

  {
    path: '/ar/dunning',
    name: 'ar-dunning',
    component: () => import('@/modules/ar/DunningView.vue'),
    meta: { titleKey: 'ar.routeTitles.arDunning', permission: AR_PERMISSIONS.dunningRead },
  },

  {
    path: '/ar/reports/aging',
    name: 'ar-aging',
    component: () => import('@/modules/ar/AgingReportView.vue'),
    meta: { titleKey: 'ar.routeTitles.arAging', permission: AR_PERMISSIONS.reportRead },
  },
  {
    path: '/ar/reports/dso',
    name: 'ar-dso',
    component: () => import('@/modules/ar/DsoReportView.vue'),
    meta: { titleKey: 'ar.routeTitles.arDso', permission: AR_PERMISSIONS.reportRead },
  },
  {
    path: '/ar/reports/statement',
    name: 'ar-statement',
    component: () => import('@/modules/ar/StatementView.vue'),
    meta: { titleKey: 'ar.routeTitles.arStatement', permission: AR_PERMISSIONS.reportRead },
  },

  /* -------------------------------------------- C3: expected credit loss */

  {
    path: '/ar/ecl/matrices',
    name: 'ar-ecl-matrices',
    component: () => import('@/modules/ar/EclMatricesView.vue'),
    meta: { titleKey: 'ar.routeTitles.eclMatrices', permission: AR_PERMISSIONS.eclRead },
  },
  {
    path: '/ar/ecl/runs',
    name: 'ar-ecl-runs',
    component: () => import('@/modules/ar/EclRunsView.vue'),
    meta: { titleKey: 'ar.routeTitles.eclRuns', permission: AR_PERMISSIONS.eclRead },
  },
  {
    path: '/ar/ecl/runs/:id(\\d+)',
    name: 'ar-ecl-run',
    component: () => import('@/modules/ar/EclRunView.vue'),
    meta: { titleKey: 'ar.routeTitles.eclRun', permission: AR_PERMISSIONS.eclRead },
  },
  {
    path: '/ar/ecl/assessments',
    name: 'ar-ecl-assessments',
    component: () => import('@/modules/ar/EclAssessmentsView.vue'),
    meta: { titleKey: 'ar.routeTitles.eclAssessments', permission: AR_PERMISSIONS.eclRead },
  },
  {
    path: '/ar/reports/ecl-movement',
    name: 'ar-ecl-movement',
    component: () => import('@/modules/ar/EclMovementReportView.vue'),
    meta: { titleKey: 'ar.routeTitles.eclMovement', permission: AR_PERMISSIONS.reportRead },
  },
  {
    path: '/ar/customers/:id(\\d+)/position',
    name: 'ar-customer-position',
    component: () => import('@/modules/ar/CustomerPositionView.vue'),
    meta: { titleKey: 'ar.routeTitles.customerPosition', permission: AR_PERMISSIONS.customerRead },
  },
]
